import type { ModelInfo } from '@paradigm/terminal-core';

/**
 * Id substring that marks a model image-capable when the catalog carries no
 * usable capability data. Mirrors the setup script's BUILT-IN default for
 * OPENCLAW_VISION_PREFIXES (`bedrock/anthropic`, used by un-templated
 * deploys) — the fleet itself sets `OPENCLAW_VISION_PREFIXES: "*"` (openclaw
 * declares every model image-capable and does no gating of its own; this UI
 * gate is the only image filter), so this heuristic exists purely to pick a
 * sensible UI default, not to mirror gateway behavior.
 */
const LEGACY_VISION_ID_SUBSTRING = 'bedrock/anthropic';

/**
 * Whether the currently selected model accepts image input, so the composer
 * can gate the attach affordance. An image sent to a genuinely text-only
 * model fails in a mode set by OPENCLAW_VISION_PREFIXES (see
 * infra/helm/terminal/values.yaml): a visible provider error under the
 * fleet's "*", a silent offload-and-ignore under an id-prefix list. This
 * gate exists so users hit neither.
 *
 * Source of truth is the catalog's `supports` flags. `selectedId == null`
 * means the gateway default (OPENCLAW_PRIMARY_MODEL, an Anthropic model
 * fleet-wide) → image-capable.
 */
export function modelSupportsImages(
  models: readonly ModelInfo[],
  selectedId: string | null,
): boolean {
  if (selectedId == null) return true;
  const model = models.find((m) => m.id === selectedId);
  const s = model?.supports;
  // The sidecar always serializes all three flags — but its non-LiteLLM
  // fallback catalogs (config-file snapshot, OPENCLAW_MODELS env) zero them
  // out wholesale. An all-false `supports` therefore means "unknown", not
  // "definitely can't": trusting it would hide the image affordance
  // fleet-wide whenever LiteLLM is briefly unreachable. Only a `supports`
  // with at least one true flag is real capability data.
  if (
    s != null &&
    (s.vision === true || s.audioInput === true || s.pdfInput === true)
  ) {
    return s.vision === true;
  }
  // No capability data (missing, all-false fallback catalog, or an id not in
  // the catalog): fall back to the id heuristic — which also returns the
  // right answer for genuinely text-only models like DeepSeek/GLM/Qwen.
  return (model?.id ?? selectedId).includes(LEGACY_VISION_ID_SUBSTRING);
}
